import { Remote, transfer, wrap } from 'comlink';
import { Box3, BufferAttribute, BufferGeometry, TypedArray } from 'three';
import { MeshBVH, SerializedBVH } from 'three-mesh-bvh';

import { Data } from './generate';

type GenerateFc = (data: Data) => Promise<WorkerResponse>;

interface WorkerResponse {
  error: any;
  serialized: SerializedBVH;
  position: TypedArray;
}

export class GenerateMeshBVHWorker {
  running = false;
  generateBVHWorker: Remote<GenerateFc> | null = null;

  constructor() {
    const worker = new Worker(new URL('./generate.ts', import.meta.url), { type: 'module' });
    this.generateBVHWorker = wrap<GenerateFc>(worker);
  }

  static deserializeBVH = (data: WorkerResponse, geometry: BufferGeometry, options = {}) => {
    const { serialized, position } = data;
    const bvh = MeshBVH.deserialize(serialized, geometry, { setIndex: false });
    const boundsOptions = Object.assign(
      {
        setBoundingBox: true,
      },
      options
    );

    // warning: 此处不能使用 copy，因为会报错调用一个已经 detached ArrayBuffer
    // @ts-ignore
    geometry.attributes.position.array = position;

    if (geometry.index) {
      geometry.index.array = serialized.index as TypedArray;
    } else {
      const newIndex = new BufferAttribute(serialized.index as TypedArray, 1, false);
      geometry.setIndex(newIndex);
    }
    if (boundsOptions.setBoundingBox) {
      geometry.boundingBox = bvh.getBoundingBox(new Box3());
    }
    return bvh;
  };

  async generate(geometry: BufferGeometry, options = {}) {
    if (this.running) {
      throw new Error('GenerateMeshBVHWorker: Already running job.');
    }

    if (this.generateBVHWorker === null) {
      throw new Error('GenerateMeshBVHWorker: Worker has been disposed.');
    }

    this.running = true;
    const { generateBVHWorker } = this;

    const index = geometry.index ? geometry.index.array : undefined;
    const position = geometry.attributes.position.array;

    const transferable = [position];

    if (index) {
      transferable.push(index);
    }

    const workerResponse = await generateBVHWorker(
      transfer<Data>(
        {
          index,
          position,
          options: {
            ...options,
            groups: [...geometry.groups],
          },
        },
        transferable
          .map((arr) => arr.buffer)
          .filter((v) => typeof SharedArrayBuffer === 'undefined' || !(v instanceof SharedArrayBuffer))
      )
    );

    this.running = false;

    if (workerResponse.error) {
      throw new Error(workerResponse.error);
    }

    if (workerResponse.serialized) {
      return GenerateMeshBVHWorker.deserializeBVH(workerResponse, geometry, options);
    }
  }
}
